/**
 * 愛知県の背景ツアー「愛知めぐり」コンテンツ。
 *
 * 軸は一日の時間。渥美半島の日の出から、同じ半島の日没へ。
 * 名古屋市内は3か所にとどめ、三河・知多・奥三河に7か所を配している。
 *
 * 名古屋城について。
 *  天守は耐震性の問題で閉館している。「登れる」「天守から見える」と書かない。
 *  絵は本丸御殿の外観だけにする。復元された障壁画は現代の作者の仕事なので、
 *  障壁画そのものを写実的に描かせない。
 *
 * 犬山城について。
 *  文化庁・愛知県・犬山城公式のどのページにも「現存天守」という語は無かった。
 *  使えるのは「国宝」「全国に5つしかない、国宝天守のひとつ」「三重四階地下二階の望楼型」まで。
 *  「現存天守12城」とは書かない。
 *
 * 企業の施設は出さない。
 *  半田運河の企業系ミュージアム、自動車メーカー系の博物館、博物館明治村、
 *  名古屋テレビ塔はいずれも扱わない。佐久島の現代アート作品も同じ理由で外した。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 犬山城天守の高さ、伊良湖岬灯台の高さは公式で確認できなかった。書かない。
 *  - 茶臼山高原の芝桜の品種数は、同じ公式ページ内で「7種類」「6種類」と食い違う。数を書かない。
 *  - 有松の保存地区の面積・種別、塗籠造や虫籠窓などの語は名古屋市公式に無い。書かない。
 *  - 恋路ヶ浜の「日本の渚百選」はどの百選か特定できなかった。書かない。
 *  - 拝観料・開館時間・祭りの日程など、時間で変わるものは書かない。
 */

import type { StageTour } from '../types';

export const aichiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'hii-no-sekimon',
      name: '日出の石門',
      area: '田原市・日の出',
      image: '/assets/scenery/aichi/scenes/01_hii_no_sekimon.webp',
      hotspots: [
        {
          id: 'sekimon-shinshoku',
          x: 0.5,
          y: 0.46,
          title: '波が開けた穴',
          body: '太平洋の荒波に削られて、岩の真ん中が洞穴のように抜けた奇石です。沖合に立つ沖の石門と、岸辺の岸の石門があります。',
          source: 'https://www.taharakankou.gr.jp/spot/spot.php?spot_id=7',
          mapQuery: '日出の石門',
        },
        {
          id: 'sekimon-asahi',
          x: 0.74,
          y: 0.3,
          title: '穴を抜ける朝日',
          body: '恋路ヶ浜からは二月と十月に沖の石門越しの日の出が、日出海岸の岸の石門前からは十二月中旬から一月上旬に穴を通した日の出が見えます。',
          source: 'https://www.taharakankou.gr.jp/spot/spot.php?spot_id=7',
          mapQuery: '日出の石門 日の出',
        },
        {
          id: 'koijigahama',
          x: 0.24,
          y: 0.62,
          title: '一キロの砂浜',
          body: '伊良湖岬灯台からここまで、恋路ヶ浜と呼ばれる白い砂浜が約一キロ続きます。島崎藤村の抒情詩の舞台として紹介されている浜です。',
          source: 'https://www.taharakankou.gr.jp/spot/spot.php?search_category1=&spot_id=6',
          mapQuery: '恋路ヶ浜',
        },
      ],
    },
    {
      id: 'fujimae-higata',
      name: '藤前干潟',
      area: '名古屋市・朝',
      image: '/assets/scenery/aichi/scenes/02_fujimae_higata.webp',
      hotspots: [
        {
          id: 'fujimae-ramsar',
          x: 0.46,
          y: 0.5,
          title: 'ラムサール条約の湿地',
          body: '2002年11月18日に323ヘクタールの区域が藤前干潟としてラムサール条約に登録されました。庄内川河口干潟・新川河口干潟・藤前地先干潟の三つからなります。',
          source: 'https://www.city.nagoya.jp/kankyo/page/0000067130.html',
          mapQuery: '藤前干潟',
        },
        {
          id: 'fujimae-shigi',
          x: 0.72,
          y: 0.34,
          title: '渡り鳥の中継地',
          body: 'シギ・チドリ類の東アジア・オーストラリア渡りルート上の重要な中継地で、春と秋の渡りの時期や越冬期にはシギ・チドリ類のほかカモ類も多く訪れます。',
          source: 'https://www.env.go.jp/nature/ramsar/conv/waterfowl/fujimae.html',
          mapQuery: '藤前干潟 野鳥',
        },
        {
          id: 'fujimae-shiosai',
          x: 0.24,
          y: 0.64,
          title: '潮の満ち引き',
          body: '水深はゼロから4メートル、潮汐の変化は平均水位でプラス1.21メートルからマイナス1.36メートル。区域の東側の庄内川河口干潟のほうが地盤が少し高く、干潟が見える機会が多くなります。',
          source: 'https://www.city.nagoya.jp/kankyo/page/0000067130.html',
          mapQuery: '藤前干潟 干潮',
        },
      ],
    },
    {
      id: 'arimatsu',
      name: '有松の町並み',
      area: '名古屋市・午前',
      image: '/assets/scenery/aichi/scenes/03_arimatsu.webp',
      hotspots: [
        {
          id: 'arimatsu-judenken',
          x: 0.5,
          y: 0.5,
          title: '重要伝統的建造物群保存地区',
          body: '平成28年7月25日に、有松地区が重要伝統的建造物群保存地区に選定されました。',
          source: 'https://www.city.nagoya.jp/kankou/rekishi/1017254/1017261.html',
          mapQuery: '有松 町並み',
        },
        {
          id: 'arimatsu-shukuba',
          x: 0.24,
          y: 0.36,
          title: '東海道の間に開かれた町',
          body: '有松は慶長13年（1608年）、東海道の鳴海宿と池鯉鮒宿の間に尾張藩によって開かれました。',
          source: 'https://www.city.nagoya.jp/kankou/rekishi/1017254/1017261.html',
          mapQuery: '有松',
        },
        {
          id: 'arimatsu-shibori',
          x: 0.76,
          y: 0.62,
          title: '有松絞り',
          body: '東海道を往来する旅人の土産物として絞り染めが考案され、有松絞りとともに町は発展しました。街道に面した主屋は木造2階建、切妻屋根、桟瓦葺、平入りを基本としています。',
          source: 'https://www.city.nagoya.jp/kankou/rekishi/1017254/1017261.html',
          mapQuery: '有松絞り',
        },
      ],
    },
    {
      id: 'inuyamajo',
      name: '国宝 犬山城',
      area: '犬山市・昼前',
      image: '/assets/scenery/aichi/scenes/04_inuyamajo.webp',
      hotspots: [
        {
          id: 'inuyama-kokuho',
          x: 0.5,
          y: 0.4,
          title: '五つの国宝天守のひとつ',
          body: '犬山城の天守は国宝に指定されています。愛知県公式は「全国に5つしかない、国宝天守のひとつです」と紹介しています。',
          source: 'https://rekishi-kanko.pref.aichi.jp/column/4256-2.html',
          mapQuery: '犬山城',
        },
        {
          id: 'inuyama-kouzou',
          x: 0.24,
          y: 0.56,
          title: '三重四階地下二階',
          body: '構造形式は三重四階、地下二階付、本瓦葺で、南面および西面に一重の附櫓がつきます。時代は桃山、1601年。昭和10年に重要文化財、昭和27年に国宝に指定されました。',
          source: 'https://online.bunka.go.jp/heritages/detail/200169',
          mapQuery: '犬山城 天守',
        },
        {
          id: 'inuyama-kisogawa',
          x: 0.76,
          y: 0.66,
          title: '木曽川の断崖',
          body: '木曽川の南岸、標高約85メートルの断崖の上に建ちます。天守は三重四階地下二階の望楼型です。',
          source: 'https://rekishi-kanko.pref.aichi.jp/column/4256-2.html',
          mapQuery: '犬山城 木曽川',
        },
      ],
    },
    {
      id: 'nagoyajo-honmaru',
      name: '名古屋城 本丸御殿',
      area: '名古屋市・正午',
      image: '/assets/scenery/aichi/scenes/05_nagoyajo_honmaru.webp',
      hotspots: [
        {
          id: 'honmaru-fukugen',
          x: 0.5,
          y: 0.52,
          title: '2018年に復元',
          body: '本丸御殿は2018年（平成30年）に、往時の姿を忠実に復元した空間として完成しました。',
          source: 'https://www.nagoyajo.city.nagoya.jp/guide/honmarugoten/',
          mapQuery: '名古屋城 本丸御殿',
        },
        {
          id: 'honmaru-shoin',
          x: 0.24,
          y: 0.38,
          title: '武家風書院造',
          body: '建築様式は江戸時代初期に完成したといわれる武家風書院造で、部屋の格式や用途によって天井や欄間、飾金具、障壁画などの意匠がさまざまに変化します。',
          source: 'https://www.nagoyajo.city.nagoya.jp/guide/honmarugoten/',
          mapQuery: '名古屋城',
        },
        {
          id: 'honmaru-tenshu',
          x: 0.76,
          y: 0.64,
          title: '天守は閉館中',
          body: '天守閣は耐震性が低いことに対応するため閉館しています。名古屋城公式は、閉館中も近世城郭御殿の最高傑作である本丸御殿など多くの見どころがあると案内しています。',
          source: 'https://www.nagoyajo.city.nagoya.jp/guide/tenshu/',
          mapQuery: '名古屋城 天守',
        },
      ],
    },
    {
      id: 'imo-shitsugen',
      name: '葦毛湿原',
      area: '豊橋市・9月の午後',
      image: '/assets/scenery/aichi/scenes/06_imo_shitsugen.webp',
      hotspots: [
        {
          id: 'imo-yusui',
          x: 0.46,
          y: 0.54,
          title: '国内最大級の湧水湿地',
          body: '約3.2ヘクタールの小湿原で、湧水湿地としては国内最大級の広さを誇ります。「東海のミニ尾瀬」として親しまれています。',
          source: 'https://www.city.toyohashi.lg.jp/11578.htm',
          mapQuery: '葦毛湿原',
        },
        {
          id: 'imo-shiratama',
          x: 0.72,
          y: 0.36,
          title: 'シラタマホシクサ',
          body: 'カザグルマは6月ごろ、シラタマホシクサとマアザミは9月ごろが見どころです。',
          source: 'https://www.city.toyohashi.lg.jp/11578.htm',
          mapQuery: '葦毛湿原 シラタマホシクサ',
        },
        {
          id: 'imo-shitei',
          x: 0.24,
          y: 0.66,
          title: '国の天然記念物',
          body: '2021年10月12日に国の史跡名勝天然記念物に指定されました。東海地方に固有あるいは隔離分布する植物の主要な生育地で、寒地系・暖地系・大陸系の植物が混在して生育する点が評価されています。',
          source: 'https://online.bunka.go.jp/heritages/detail/573032',
          mapQuery: '葦毛湿原 天然記念物',
        },
      ],
    },
    {
      id: 'chausuyama-shibazakura',
      name: '茶臼山高原 芝桜の丘',
      area: '豊根村・春の午後',
      image: '/assets/scenery/aichi/scenes/07_chausuyama_shibazakura.webp',
      hotspots: [
        {
          id: 'chausu-hagitaro',
          x: 0.5,
          y: 0.5,
          title: '萩太郎山の頂上付近',
          body: '標高1,358メートルの萩太郎山の頂上付近に広がります。',
          source: 'https://www.chausuyama.jp/enjoy/2014021912102489.html',
          mapQuery: '茶臼山高原 芝桜の丘',
        },
        {
          id: 'chausu-40man',
          x: 0.24,
          y: 0.64,
          title: '40万株',
          body: '22,000平方メートルの敷地に40万株の芝桜が咲き誇ります。',
          source: 'https://www.chausuyama.jp/enjoy/2014021912102489.html',
          mapQuery: '茶臼山高原',
        },
        {
          id: 'chausu-lift',
          x: 0.76,
          y: 0.32,
          title: '標高差約150メートル',
          body: '観光リフトは緑地広場から萩太郎山の山頂付近まで、標高差約150メートルを約12分で登ります。',
          source: 'https://www.chausuyama.jp/enjoy/2014021912102489.html',
          mapQuery: '茶臼山高原 リフト',
        },
      ],
    },
    {
      id: 'korankei',
      name: '香嵐渓',
      area: '豊田市・11月の夕方前',
      image: '/assets/scenery/aichi/scenes/08_korankei.webp',
      hotspots: [
        {
          id: 'korankei-3000',
          x: 0.5,
          y: 0.44,
          title: '約3,000本のもみじ',
          body: '約3,000本のもみじが赤や黄に染まる様は圧巻です。もみじまつりは11月1日から約1か月間行われます。',
          source: 'https://www.city.toyota.aichi.jp/shisei/jichiku/1008649/1013511/1005336.html',
          mapQuery: '香嵐渓',
        },
        {
          id: 'korankei-sanei',
          x: 0.24,
          y: 0.6,
          title: '寛永11年に植えられた',
          body: '香嵐渓のもみじは、香積寺十一世の三栄和尚が寛永11年（1634年）に植えたのが始まりとされます。和尚は巴川ぞいの参道から境内にかけて楓や杉を植えました。',
          source: 'https://asuke.info/korankei/',
          mapQuery: '香嵐渓 もみじ',
        },
        {
          id: 'korankei-kaede',
          x: 0.76,
          y: 0.3,
          title: '11種類の楓',
          body: '香嵐渓には11種類の楓があるといわれ、イロハカエデ・ヤマモミジ・ウラゲエンコウカエデ・オオモミジ・コハウチワカエデなどが多くあります。春はかたくりが3月中旬から4月上旬に見頃です。',
          source: 'https://asuke.info/korankei/',
          mapQuery: '香嵐渓 カタクリ',
        },
      ],
    },
    {
      id: 'handa-unga',
      name: '半田運河 蔵のまち',
      area: '半田市・夕',
      image: '/assets/scenery/aichi/scenes/09_handa_unga.webp',
      hotspots: [
        {
          id: 'handa-jozo',
          x: 0.5,
          y: 0.5,
          title: '酒と酢を江戸へ運んだ運河',
          body: '江戸時代、海運で栄えた半田から、江戸へと特産の酒や酢が運ばれました。運河沿いには、その醸造蔵が建ち並んでいます。',
          source: 'https://www.handa-kankou.com/spot/kura/',
          mapQuery: '半田運河',
        },
        {
          id: 'handa-kurobe',
          x: 0.24,
          y: 0.38,
          title: '黒壁の蔵',
          body: '運河沿いに立ち並ぶ黒壁の蔵の風景が特徴で、歴史的な建造物も保存され、江戸の面影を今に伝えています。',
          source: 'https://www.handa-kankou.com/spot/kura/',
          mapQuery: '半田市 蔵のまち',
        },
        {
          id: 'handa-koinobori',
          x: 0.76,
          y: 0.26,
          title: '50匹以上の鯉のぼり',
          body: '毎年4月中旬から5月5日まで、半田運河に50匹以上の鯉のぼりが泳ぎます。',
          source: 'https://www.handa-kankou.com/spot/kura/',
          mapQuery: '半田運河 鯉のぼり',
        },
      ],
    },
    {
      id: 'atsumi-nanohana',
      name: '渥美半島の菜の花',
      area: '田原市・冬の日没',
      image: '/assets/scenery/aichi/scenes/10_atsumi_nanohana.webp',
      hotspots: [
        {
          id: 'nanohana-1100man',
          x: 0.5,
          y: 0.56,
          title: '1月から3月に1100万本',
          body: '1月から3月にかけて約1100万本の菜の花が咲き誇ります。',
          source: 'https://www.pref.aichi.jp/site/kaidou/nanohana.html',
          mapQuery: '渥美半島 菜の花',
        },
        {
          id: 'nanohana-60km',
          x: 0.24,
          y: 0.4,
          title: '約60キロのルート',
          body: '国道42号と国道259号を軸として渥美半島を巡る、約60キロのルートです。',
          source: 'https://www.pref.aichi.jp/site/kaidou/nanohana.html',
          mapQuery: '渥美半島',
        },
        {
          id: 'nanohana-himawari',
          x: 0.76,
          y: 0.3,
          title: '夏はひまわり',
          body: '菜の花の開花時期に合わせて渥美半島菜の花まつりが開かれます。ひまわりは6月から8月が見頃です。',
          source: 'https://www.pref.aichi.jp/site/kaidou/nanohana.html',
          mapQuery: '渥美半島 ひまわり',
        },
      ],
    },
  ],
};
